import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {Calendar, Car, Clock, AlertTriangle, CheckCircle2, Wrench, LogOut, LogIn, RotateCcw, Phone, Edit, Trash2, CreditCard, Plus, Search, FileText, } from 'lucide-react'

type Reservation = {
  _id: string
  reservationNumber?: string
  vehicleId?: string
  vehicleName?: string
  clientName?: string
  clientPhone?: string
  clientEmail?: string
  startDate: string
  endDate: string
  totalPrice?: number
  status: string
  notes?: string
  createdAt?: string
  pickupLocation?: string
  returnLocation?: string
}

type Props = {
  reservations: Reservation[]
  loading: boolean
  onEdit: (id: string) => void
  onDelete: (id: string) => void
  onStatusChange: (id: string, newStatus: string) => void
  onCreate: () => void
}

type TabKey =
  | 'today'
  | 'to_prepare'
  | 'pickups'
  | 'active'
  | 'returns'
  | 'anomalies'
  | 'upcoming'
  | 'completed'
  | 'all'

const STATUS_LABEL: Record<string, string> = {
  pending: 'En attente',
  confirmed: 'Confirmée',
  paid: 'Payée',
  active: 'En cours',
  completed: 'Terminée',
  cancelled: 'Annulée',
}

const STATUS_COLOR: Record<string, string> = {
  pending: 'bg-yellow-500/90 text-black',
  confirmed: 'bg-blue-500/90 text-white',
  paid: 'bg-green-500/90 text-white',
  active: 'bg-purple-500/90 text-white',
  completed: 'bg-gray-500/90 text-white',
  cancelled: 'bg-red-500/90 text-white',
}

// Utilitaires date
const startOfDay = (d: Date) => {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}
const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate()

const formatDate = (iso?: string) => {
  if (!iso) return '-'
  try {
    return new Date(iso).toLocaleDateString('fr-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  } catch {
    return iso
  }
}
const formatTime = (iso?: string) => {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleTimeString('fr-CH', {
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return ''
  }
}
const formatDateTime = (iso?: string) => {
  if (!iso) return '-'
  return `${formatDate(iso)} à ${formatTime(iso)}`
}

const BLOCKING_STATUSES = ['pending', 'confirmed', 'paid', 'active']

export default function OperationalReservations({
  reservations,
  loading,
  onEdit,
  onDelete,
  onStatusChange,
  onCreate,
}: Props) {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<TabKey>('today')
  const [search, setSearch] = useState('')

  const now = useMemo(() => new Date(), [])
  const today = useMemo(() => startOfDay(now), [now])
  const in48h = useMemo(() => {
    const d = new Date(today)
    d.setDate(d.getDate() + 2)
    return d
  }, [today])

  // --- Catégorisation ---
  const buckets = useMemo(() => {
    const toPrepare: Reservation[] = []
    const todayPickups: Reservation[] = []
    const active: Reservation[] = []
    const todayReturns: Reservation[] = []
    const overduePickups: Reservation[] = []
    const overdueReturns: Reservation[] = []
    const upcoming: Reservation[] = []
    const completed: Reservation[] = []

    reservations.forEach((r) => {
      if (!r.startDate || !r.endDate) return
      const start = new Date(r.startDate)
      const end = new Date(r.endDate)
      const startDay = startOfDay(start)
      const endDay = startOfDay(end)
      const status = r.status || 'pending'

      // Terminées / annulées
      if (status === 'completed' || status === 'cancelled') {
        if (status === 'completed') completed.push(r)
        return
      }

      // En cours : statut active
      if (status === 'active') {
        if (isSameDay(endDay, today)) todayReturns.push(r)
        else if (endDay < today) overdueReturns.push(r)
        else active.push(r)
        return
      }

      // Non encore pris en charge (pending/confirmed/paid)
      if (BLOCKING_STATUSES.includes(status)) {
        if (startDay < today) {
          overduePickups.push(r)
        } else if (isSameDay(startDay, today)) {
          todayPickups.push(r)
        } else if (startDay < in48h) {
          toPrepare.push(r)
        } else {
          upcoming.push(r)
        }
      }
    })

    // Tri chronologique
    const byStart = (a: Reservation, b: Reservation) =>
      new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    const byEnd = (a: Reservation, b: Reservation) =>
      new Date(a.endDate).getTime() - new Date(b.endDate).getTime()

    toPrepare.sort(byStart)
    todayPickups.sort(byStart)
    active.sort(byEnd)
    todayReturns.sort(byEnd)
    overduePickups.sort(byStart)
    overdueReturns.sort(byEnd)
    upcoming.sort(byStart)
    completed.sort(byEnd)

    return {
      toPrepare,
      todayPickups,
      active,
      todayReturns,
      overduePickups,
      overdueReturns,
      upcoming,
      completed,
    }
  }, [reservations, today, in48h])

  const anomalies = useMemo(
    () => [...buckets.overduePickups, ...buckets.overdueReturns],
    [buckets]
  )

  // Revenus prévus du jour (pickups du jour confirmés/payés)
  const todayRevenue = useMemo(
    () =>
      buckets.todayPickups.reduce((sum, r) => sum + (Number(r.totalPrice) || 0), 0),
    [buckets.todayPickups]
  )

  const filteredForTab = useMemo(() => {
    let list: Reservation[] = []
    switch (activeTab) {
      case 'today':
        list = [] // géré par le Kanban
        break
      case 'to_prepare':
        list = buckets.toPrepare
        break
      case 'pickups':
        list = buckets.todayPickups
        break
      case 'active':
        list = buckets.active
        break
      case 'returns':
        list = buckets.todayReturns
        break
      case 'anomalies':
        list = anomalies
        break
      case 'upcoming':
        list = buckets.upcoming
        break
      case 'completed':
        list = buckets.completed
        break
      case 'all':
        list = reservations
        break
    }
    const s = search.trim().toLowerCase()
    if (!s) return list
    return list.filter((r) =>
      [
        r.vehicleName,
        r.clientName,
        r.clientPhone,
        r.clientEmail,
        r.reservationNumber,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s))
    )
  }, [activeTab, buckets, anomalies, reservations, search])

  // Palettes statiques (nécessaires pour que Tailwind JIT compile bien les classes)
  const PALETTES: Record<string, {
    iconBg: string
    iconText: string
    value: string
    border: string
    borderActive: string
    shadowActive: string
    colBg: string
    colIcon: string
    colCount: string
    colCountText: string
  }> = {
    yellow: {
      iconBg: 'bg-yellow-500/20', iconText: 'text-yellow-400', value: 'text-yellow-400',
      border: 'border-yellow-400/30 hover:border-yellow-400',
      borderActive: 'border-yellow-400', shadowActive: 'shadow-yellow-400/30',
      colBg: 'from-yellow-500/30 to-yellow-600/10', colIcon: 'text-yellow-400',
      colCount: 'bg-yellow-500/30', colCountText: 'text-yellow-200',
    },
    blue: {
      iconBg: 'bg-blue-500/20', iconText: 'text-blue-400', value: 'text-blue-400',
      border: 'border-blue-400/30 hover:border-blue-400',
      borderActive: 'border-blue-400', shadowActive: 'shadow-blue-400/30',
      colBg: 'from-blue-500/30 to-blue-600/10', colIcon: 'text-blue-400',
      colCount: 'bg-blue-500/30', colCountText: 'text-blue-200',
    },
    purple: {
      iconBg: 'bg-purple-500/20', iconText: 'text-purple-400', value: 'text-purple-400',
      border: 'border-purple-400/30 hover:border-purple-400',
      borderActive: 'border-purple-400', shadowActive: 'shadow-purple-400/30',
      colBg: 'from-purple-500/30 to-purple-600/10', colIcon: 'text-purple-400',
      colCount: 'bg-purple-500/30', colCountText: 'text-purple-200',
    },
    green: {
      iconBg: 'bg-green-500/20', iconText: 'text-green-400', value: 'text-green-400',
      border: 'border-green-400/30 hover:border-green-400',
      borderActive: 'border-green-400', shadowActive: 'shadow-green-400/30',
      colBg: 'from-green-500/30 to-green-600/10', colIcon: 'text-green-400',
      colCount: 'bg-green-500/30', colCountText: 'text-green-200',
    },
    red: {
      iconBg: 'bg-red-500/20', iconText: 'text-red-400', value: 'text-red-400',
      border: 'border-red-400/30 hover:border-red-400',
      borderActive: 'border-red-400', shadowActive: 'shadow-red-400/30',
      colBg: 'from-red-500/30 to-red-600/10', colIcon: 'text-red-400',
      colCount: 'bg-red-500/30', colCountText: 'text-red-200',
    },
    orange: {
      iconBg: 'bg-orange-500/20', iconText: 'text-orange-400', value: 'text-orange-400',
      border: 'border-orange-400/30 hover:border-orange-400',
      borderActive: 'border-orange-400', shadowActive: 'shadow-orange-400/30',
      colBg: 'from-orange-500/30 to-orange-600/10', colIcon: 'text-orange-400',
      colCount: 'bg-orange-500/30', colCountText: 'text-orange-200',
    },
    emerald: {
      iconBg: 'bg-emerald-500/20', iconText: 'text-emerald-400', value: 'text-emerald-400',
      border: 'border-emerald-400/30 hover:border-emerald-400',
      borderActive: 'border-emerald-400', shadowActive: 'shadow-emerald-400/30',
      colBg: 'from-emerald-500/30 to-emerald-600/10', colIcon: 'text-emerald-400',
      colCount: 'bg-emerald-500/30', colCountText: 'text-emerald-200',
    },
  }

  // --- UI sub-components ---
  const KpiCard = ({
    label,
    value,
    icon: Icon,
    color,
    tabKey,
    hint,
  }: {
    label: string
    value: number | string
    icon: any
    color: keyof typeof PALETTES
    tabKey?: TabKey
    hint?: string
  }) => {
    const p = PALETTES[color]
    const isActive = tabKey && activeTab === tabKey
    return (
      <button
        type="button"
        onClick={() => tabKey && setActiveTab(tabKey)}
        className={`text-left bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-5 border-2 transition-all hover:scale-[1.02] hover:shadow-2xl ${
          isActive ? `${p.borderActive} ${p.shadowActive} shadow-2xl` : p.border
        }`}
      >
        <div className="flex items-center justify-between mb-2">
          <div className={`p-2.5 rounded-xl ${p.iconBg}`}>
            <Icon className={`w-5 h-5 ${p.iconText}`} />
          </div>
          <span className={`text-3xl font-black ${p.value}`}>{value}</span>
        </div>
        <p className="text-sm font-bold text-white">{label}</p>
        {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
      </button>
    )
  }

  // Pickup / Préparation / Retour actions
  const handleMarkPickedUp = (r: Reservation) => {
    if (window.confirm(`Confirmer la prise en charge du véhicule "${r.vehicleName}" par ${r.clientName} ?`)) {
      onStatusChange(r._id, 'active')
    }
  }
  const handleMarkReturned = (r: Reservation) => {
    if (window.confirm(`Confirmer le retour du véhicule "${r.vehicleName}" ?`)) {
      onStatusChange(r._id, 'completed')
    }
  }
  const handleMarkConfirmed = (r: Reservation) => {
    onStatusChange(r._id, 'confirmed')
  }

  // Carte de réservation compacte
  const ReservationCard = ({
    r,
    tone = 'default',
    primaryAction,
  }: {
    r: Reservation
    tone?: 'default' | 'warning' | 'danger' | 'success' | 'info'
    primaryAction?: { label: string; icon: any; onClick: () => void; color: string }
  }) => {
    const toneBorder =
      tone === 'danger'
        ? 'border-red-500/60'
        : tone === 'warning'
        ? 'border-orange-400/60'
        : tone === 'success'
        ? 'border-green-400/60'
        : tone === 'info'
        ? 'border-blue-400/60'
        : 'border-gray-700 hover:border-yellow-400/50'

    const isOverduePickup =
      BLOCKING_STATUSES.includes(r.status) &&
      r.status !== 'active' &&
      new Date(r.startDate) < now
    const isOverdueReturn =
      r.status === 'active' && new Date(r.endDate) < now

    return (
      <div
        className={`bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl overflow-hidden border-2 ${toneBorder} transition-all`}
      >
        <div className="p-4">
          {/* Header véhicule + statut */}
          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Car className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                <h4 className="text-sm font-black text-white truncate">
                  {r.vehicleName || 'Véhicule'}
                </h4>
              </div>
              {r.reservationNumber && (
                <p className="text-[10px] text-gray-500 font-mono">
                  #{r.reservationNumber}
                </p>
              )}
            </div>
            <span
              className={`px-2 py-1 rounded-md text-[10px] font-bold shadow-lg whitespace-nowrap ${
                STATUS_COLOR[r.status] || 'bg-gray-500 text-white'
              }`}
            >
              {STATUS_LABEL[r.status] || r.status}
            </span>
          </div>

          {/* Alertes */}
          {isOverduePickup && (
            <div className="mb-3 bg-red-500/20 border border-red-500/40 rounded-lg p-2 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
              <p className="text-xs text-red-300 font-bold">
                Prise en charge en retard
              </p>
            </div>
          )}
          {isOverdueReturn && (
            <div className="mb-3 bg-red-500/20 border border-red-500/40 rounded-lg p-2 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
              <p className="text-xs text-red-300 font-bold">Retour en retard</p>
            </div>
          )}

          {/* Client */}
          <div className="mb-3">
            <p className="text-xs text-gray-400 mb-0.5">Client</p>
            <p className="text-sm text-white font-bold truncate">
              {r.clientName || '-'}
            </p>
            {r.clientPhone && (
              <a
                href={`tel:${r.clientPhone}`}
                className="inline-flex items-center gap-1 text-xs text-yellow-400 hover:text-yellow-300 font-mono mt-0.5"
              >
                <Phone className="w-3 h-3" />
                {r.clientPhone}
              </a>
            )}
          </div>

          {/* Période */}
          <div className="mb-3 bg-black/30 rounded-lg p-2.5 border border-gray-700">
            <div className="flex items-center gap-2 text-xs text-gray-300">
              <LogIn className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
              <span className="font-bold text-green-300">Départ :</span>
              <span>{formatDateTime(r.startDate)}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-300 mt-1">
              <LogOut className="w-3.5 h-3.5 text-orange-400 flex-shrink-0" />
              <span className="font-bold text-orange-300">Retour :</span>
              <span>{formatDateTime(r.endDate)}</span>
            </div>
          </div>

          {/* Prix + création */}
          <div className="flex items-center justify-between mb-3 text-xs">
            <span className="text-gray-400">
              Réservée le {formatDate(r.createdAt)}
            </span>
            {typeof r.totalPrice === 'number' && (
              <span className="font-black text-yellow-400">
                CHF {r.totalPrice}
              </span>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2 flex-wrap">
            {primaryAction && (
              <button
                onClick={primaryAction.onClick}
                className={`flex items-center gap-1.5 flex-1 justify-center px-3 py-2 ${primaryAction.color} rounded-lg text-xs font-bold transition-all hover:scale-[1.02] shadow-lg`}
              >
                <primaryAction.icon className="w-3.5 h-3.5" />
                {primaryAction.label}
              </button>
            )}
            <button
              onClick={() => onEdit(r._id)}
              title="Modifier"
              className="p-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-all hover:scale-105 shadow-lg"
            >
              <Edit className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => onDelete(r._id)}
              title="Supprimer"
              className="p-2 bg-red-600/80 hover:bg-red-500 text-white rounded-lg transition-all hover:scale-105 shadow-lg"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Colonne Kanban
  const KanbanColumn = ({
    title,
    subtitle,
    color,
    icon: Icon,
    items,
    renderAction,
    emptyText,
  }: {
    title: string
    subtitle: string
    color: keyof typeof PALETTES
    icon: any
    items: Reservation[]
    renderAction?: (r: Reservation) => { label: string; icon: any; onClick: () => void; color: string } | undefined
    emptyText: string
  }) => {
    const p = PALETTES[color]
    return (
      <div className="flex flex-col bg-gray-900/40 rounded-2xl border border-gray-700 overflow-hidden min-h-[400px]">
        <div className={`bg-gradient-to-r ${p.colBg} p-4 border-b border-gray-700`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Icon className={`w-5 h-5 ${p.colIcon}`} />
              <h3 className="text-sm font-black text-white uppercase tracking-wide">
                {title}
              </h3>
            </div>
            <span className={`px-2.5 py-0.5 rounded-full ${p.colCount} ${p.colCountText} text-xs font-black`}>
              {items.length}
            </span>
          </div>
          <p className="text-[11px] text-gray-400 mt-1">{subtitle}</p>
        </div>
        <div className="flex-1 p-3 space-y-3 overflow-y-auto max-h-[700px]">
          {items.length === 0 ? (
            <div className="text-center py-10 text-xs text-gray-500 italic">
              {emptyText}
            </div>
          ) : (
            items.map((r) => (
              <ReservationCard
                key={r._id}
                r={r}
                primaryAction={renderAction?.(r)}
                tone={
                  color === 'red'
                    ? 'danger'
                    : color === 'orange' || color === 'yellow'
                    ? 'warning'
                    : color === 'green'
                    ? 'success'
                    : color === 'blue'
                    ? 'info'
                    : 'default'
                }
              />
            ))
          )}
        </div>
      </div>
    )
  }

  // --- Rendu ---
  if (loading) {
    return (
      <div className="text-center py-20">
        <div className="inline-block w-16 h-16 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin mb-4" />
        <div className="text-xl text-white">Chargement des réservations...</div>
      </div>
    )
  }

  return (
    <div>
      {/* Header + Recherche */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-3xl font-black text-white flex items-center gap-3">
            <Calendar className="w-8 h-8 text-yellow-400" />
            Suivi des locations
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Vue opérationnelle — {formatDate(now.toISOString())}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher client, plaque, n°..."
              className="pl-9 pr-4 py-2.5 bg-gray-800 border-2 border-gray-700 rounded-xl text-white text-sm focus:border-yellow-400 focus:outline-none w-full md:w-72"
            />
          </div>
          <button
            onClick={onCreate}
            className="flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black px-5 py-2.5 rounded-xl hover:shadow-2xl hover:shadow-yellow-400/50 transition-all font-bold hover:scale-105 whitespace-nowrap"
          >
            <Plus className="w-4 h-4" />
            Nouvelle
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        <KpiCard
          label="À préparer"
          hint="dans les 48h"
          value={buckets.toPrepare.length}
          icon={Wrench}
          color="yellow"
          tabKey="to_prepare"
        />
        <KpiCard
          label="Départs jour"
          hint="aujourd'hui"
          value={buckets.todayPickups.length}
          icon={LogIn}
          color="blue"
          tabKey="pickups"
        />
        <KpiCard
          label="En cours"
          hint="locations actives"
          value={buckets.active.length}
          icon={Car}
          color="purple"
          tabKey="active"
        />
        <KpiCard
          label="Retours jour"
          hint="aujourd'hui"
          value={buckets.todayReturns.length}
          icon={LogOut}
          color="green"
          tabKey="returns"
        />
        <KpiCard
          label="Anomalies"
          hint="retards"
          value={anomalies.length}
          icon={AlertTriangle}
          color="red"
          tabKey="anomalies"
        />
        <KpiCard
          label="À venir"
          hint="> 48h"
          value={buckets.upcoming.length}
          icon={Calendar}
          color="orange"
          tabKey="upcoming"
        />
        <KpiCard
          label="Revenus jour"
          hint="départs payés"
          value={`${todayRevenue}.-`}
          icon={CreditCard}
          color="emerald"
        />
      </div>

      {/* Onglets */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl shadow-xl mb-6 border-2 border-gray-700 overflow-x-auto">
        <div className="flex min-w-max">
          {(
            [
              { key: 'today', label: "Aujourd'hui", icon: Calendar },
              { key: 'to_prepare', label: 'À préparer', icon: Wrench },
              { key: 'pickups', label: 'Départs', icon: LogIn },
              { key: 'active', label: 'En cours', icon: Car },
              { key: 'returns', label: 'Retours', icon: LogOut },
              { key: 'anomalies', label: 'Anomalies', icon: AlertTriangle },
              { key: 'upcoming', label: 'À venir', icon: Clock },
              { key: 'completed', label: 'Terminées', icon: CheckCircle2 },
              { key: 'all', label: 'Toutes', icon: FileText },
            ] as { key: TabKey; label: string; icon: any }[]
          ).map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-2 px-5 py-3.5 font-bold text-sm transition-all whitespace-nowrap ${
                activeTab === t.key
                  ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-black'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
              }`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Contenu : Vue Aujourd'hui = Kanban, autres = liste filtrée */}
      {activeTab === 'today' ? (
        <>
          {anomalies.length > 0 && (
            <div className="mb-4 bg-gradient-to-r from-red-500/20 to-red-600/10 border-2 border-red-500/50 rounded-2xl p-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-red-400" />
                <div>
                  <p className="text-sm font-black text-red-300">
                    {anomalies.length} anomalie{anomalies.length > 1 ? 's' : ''} à traiter
                  </p>
                  <p className="text-xs text-red-200/80">
                    {buckets.overduePickups.length} prise(s) en charge en retard • {buckets.overdueReturns.length} retour(s) en retard
                  </p>
                </div>
              </div>
              <button
                onClick={() => setActiveTab('anomalies')}
                className="px-4 py-2 bg-red-500 hover:bg-red-400 text-white rounded-lg text-sm font-bold transition-all hover:scale-105"
              >
                Voir les détails
              </button>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <KanbanColumn
              title="À préparer"
              subtitle="Véhicules à préparer dans les 48h"
              color="yellow"
              icon={Wrench}
              items={buckets.toPrepare}
              emptyText="Aucune préparation programmée"
              renderAction={(r) =>
                r.status === 'pending'
                  ? {
                      label: 'Confirmer',
                      icon: CheckCircle2,
                      onClick: () => handleMarkConfirmed(r),
                      color: 'bg-blue-500 hover:bg-blue-400 text-white',
                    }
                  : undefined
              }
            />
            <KanbanColumn
              title="Départs du jour"
              subtitle="Prises en charge prévues aujourd'hui"
              color="blue"
              icon={LogIn}
              items={buckets.todayPickups}
              emptyText="Aucun départ aujourd'hui"
              renderAction={(r) => ({
                label: 'Prise en charge',
                icon: LogIn,
                onClick: () => handleMarkPickedUp(r),
                color: 'bg-green-500 hover:bg-green-400 text-white',
              })}
            />
            <KanbanColumn
              title="En cours"
              subtitle="Locations actuellement actives"
              color="purple"
              icon={Car}
              items={buckets.active}
              emptyText="Aucune location en cours"
              renderAction={(r) => ({
                label: 'Retour',
                icon: LogOut,
                onClick: () => handleMarkReturned(r),
                color: 'bg-orange-500 hover:bg-orange-400 text-white',
              })}
            />
            <KanbanColumn
              title="Retours du jour"
              subtitle="Véhicules attendus aujourd'hui"
              color="green"
              icon={LogOut}
              items={buckets.todayReturns}
              emptyText="Aucun retour aujourd'hui"
              renderAction={(r) => ({
                label: 'Confirmer retour',
                icon: RotateCcw,
                onClick: () => handleMarkReturned(r),
                color: 'bg-green-500 hover:bg-green-400 text-white',
              })}
            />
          </div>
        </>
      ) : (
        <div className="space-y-3">
          {filteredForTab.length === 0 ? (
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl p-16 text-center border-2 border-gray-700">
              <Calendar className="w-16 h-16 text-yellow-400 mx-auto mb-4 opacity-50" />
              <h3 className="text-xl font-bold text-white mb-2">
                Aucune réservation
              </h3>
              <p className="text-sm text-gray-400">
                Aucun élément ne correspond à cette vue.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredForTab.map((r) => {
                // Choisir une action principale contextuelle
                let primary:
                  | { label: string; icon: any; onClick: () => void; color: string }
                  | undefined
                if (BLOCKING_STATUSES.includes(r.status) && r.status !== 'active') {
                  primary = {
                    label: 'Prise en charge',
                    icon: LogIn,
                    onClick: () => handleMarkPickedUp(r),
                    color: 'bg-green-500 hover:bg-green-400 text-white',
                  }
                } else if (r.status === 'active') {
                  primary = {
                    label: 'Confirmer retour',
                    icon: LogOut,
                    onClick: () => handleMarkReturned(r),
                    color: 'bg-orange-500 hover:bg-orange-400 text-white',
                  }
                }
                return (
                  <ReservationCard
                    key={r._id}
                    r={r}
                    primaryAction={primary}
                    tone={
                      activeTab === 'anomalies'
                        ? 'danger'
                        : activeTab === 'returns'
                        ? 'success'
                        : activeTab === 'pickups'
                        ? 'info'
                        : 'default'
                    }
                  />
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
